import QuestionTypeIcon from '../../../../components/Votings/QuestionTypeIcon';
import {
  QUESTION_TYPES,
  questionTypeHint,
  questionTypeLabel,
  type QuestionType,
} from '../../../../lib/votings';
import { __ } from '../../../../utils';
import { Box, Stack, Typography } from '@presov.sk/presov-ui-react';
import React from 'react';

type Props = {
  onPick: (type: QuestionType) => void;
};

/**
 * Prázdny stav nového hlasovania.
 *
 * Prvá vec na obrazovke je otázka, nie formulár s dátumami: úradník začína
 * tým, čo sa chce občana spýtať, a názov s publikom doplní kedykoľvek pred
 * publikovaním. Predtým bola prvou obrazovkou karta „Základné informácie",
 * takže sa najskôr vypĺňal dátum otvorenia hlasovania, ktoré ešte nemalo ani
 * jednu otázku.
 *
 * „Vložiť otázky z iného hlasovania" z návrhu tu nie je - API na kópiu
 * hlasovania nemá a duplikát na klientovi by musel načítať cudzí detail
 * a prepísať ID možností, čo je iná funkcia než tlačidlo v prázdnom stave.
 */
const VotingEmptyState: React.FC<Props> = ({ onPick }) => (
  <Stack
    alignItems='center'
    justifyContent='center'
    sx={{ flex: 1, minHeight: 0, p: { xs: 2.5, md: 4 } }}
  >
    <Stack
      alignItems='center'
      gap={2.5}
      sx={{ maxWidth: 560, width: '100%', textAlign: 'center' }}
    >
      <Typography
        component='h2'
        sx={{ fontSize: 24, fontWeight: 700, lineHeight: 1.25 }}
      >
        {__('Čím sa občana spýtate?')}
      </Typography>
      <Typography
        variant='body1'
        color='text.secondary'
        sx={{ lineHeight: 1.6 }}
      >
        {__(
          'Začnite prvou otázkou. Názov, dátumy a publikum sa dajú doplniť kedykoľvek pred publikovaním.',
        )}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 1.25,
          width: '100%',
          textAlign: 'left',
        }}
      >
        {QUESTION_TYPES.map((type) => (
          <Box
            key={type}
            component='button'
            type='button'
            onClick={() => onPick(type)}
            sx={({ palette, shape }) => ({
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 1.75,
              cursor: 'pointer',
              fontFamily: 'inherit',
              textAlign: 'left',
              borderRadius: `${shape.borderRadius}px`,
              border: `1px solid ${palette.divider}`,
              backgroundColor: palette.background.paper,
              '&:hover': { borderColor: palette.grey[400] },
            })}
          >
            <Box
              sx={({ palette, shape }) => ({
                width: 34,
                height: 34,
                flex: 'none',
                display: 'grid',
                placeItems: 'center',
                borderRadius: `${shape.borderRadius}px`,
                backgroundColor: palette.grey[100],
                color: palette.grey[600],
              })}
            >
              <QuestionTypeIcon type={type} size={18} />
            </Box>
            <Stack gap={0.25}>
              <Typography component='b' sx={{ fontSize: 14, fontWeight: 700 }}>
                {questionTypeLabel(type)}
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                {questionTypeHint(type)}
              </Typography>
            </Stack>
          </Box>
        ))}
      </Box>
    </Stack>
  </Stack>
);

export default VotingEmptyState;
